import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { aiNav, aiNavAnchors } from "@/mocks/aiassist";
import { userHints, accessRestrictions, type WorkspaceUser } from "./roles";
import UserModal from "./usermodal";
import { ROLE_LABELS, useAuth, type Role } from "./auth";
import { api, apiErrorText } from "@/lib/api";
import { goAnchor } from "./app-nav";
import { openCompaniesOf } from "./companies";
import { t, tn, locale } from "@/lib/i18n";

/** Сводка «кто за кого отвечает» (GET /users/clients): клиенты за сотрудником и сколько открытых диалогов он видит. */
type Clients = { companies: number; contacts: number; deals: number; dialogs: number };
const EMPTY_CLIENTS: Clients = { companies: 0, contacts: 0, deals: 0, dialogs: 0 };

/* «Настройки пользователей»: участники рабочего пространства (/api/users), поиск, добавление почты Google с ролью
   и доп. правами, смена роли, отзыв и возврат доступа. Права проверяет бэкенд; здесь только прячем недоступные кнопки. */

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "•";

const lastActive = (u: WorkspaceUser) => {
  if (u.status === "disabled") return t("доступ отозван");
  if (!u.lastLoginAt) return t("ещё не входил");
  if (u.online) return t("в сети");
  const d = new Date(u.lastSeenAt || u.lastLoginAt);
  return d.toLocaleString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

const permLabel = (id: string) => accessRestrictions.find((r) => r.id === id)?.label ?? id;
const roleLabel = (role: Role) => t(ROLE_LABELS[role] ?? role);

function UserSettingsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const { user: me, viewAs } = useAuth();
  const [users, setUsers] = useState<WorkspaceUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<"add" | WorkspaceUser | null>(null);
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState("");
  // кто за какие компании/контакты/сделки отвечает сейчас (менеджер и выше)
  const [clients, setClients] = useState<{ users: Record<string, Clients>; free: Clients } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = useCallback((text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  }, []);

  const load = useCallback(async () => {
    try {
      setUsers(await api<WorkspaceUser[]>("/users"));
    } catch (err) {
      flash(apiErrorText(err));
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => {
    void load();
  }, [load]);

  const staff = me?.role === "owner" || me?.role === "admin" || me?.role === "manager";
  useEffect(() => {
    if (staff) api<NonNullable<typeof clients>>("/users/clients").then(setClients, () => setClients(null));
  }, [staff]);

  const clientsLine = (n: Clients) =>
    [tn(n.companies, "компания", "компании", "компаний"), tn(n.contacts, "контакт", "контакта", "контактов"), tn(n.deals, "сделка", "сделки", "сделок")].join(" · ");
  const dialogsLine = (n: number) => tn(n, "открытый диалог", "открытых диалога", "открытых диалогов");
  const showCompanies = (u: { id: string; name: string }) => {
    openCompaniesOf(u);
    goAnchor("#companies");
  };

  const roles = me?.can.assignRoles ?? [];
  const canEdit = (u: WorkspaceUser) => !u.owner && roles.includes(u.role) && u.id !== me?.id;
  const upsert = (u: WorkspaceUser) => setUsers((prev) => (prev.some((x) => x.id === u.id) ? prev.map((x) => (x.id === u.id ? u : x)) : [...prev, u]));

  const onNav = (id: string) => {
    if (id === "user-settings") return;
    if (id === "ai-assist") {
      goAnchor("#ai-assist");
      return;
    }
    const anchor = aiNavAnchors[id];
    if (anchor) {
      goAnchor(anchor);
      return;
    }
    flash(t("Раздел «{label}» в демо не открывается", { label: aiNav.flatMap((g) => g.items).find((x) => x.id === id)?.label ?? "" }));
  };

  const save = async (email: string, role: Role, permissions: string[]) => {
    const editing = modal && modal !== "add" ? modal : null;
    setBusy(true);
    try {
      const u = editing
        ? await api<WorkspaceUser>(`/users/${editing.id}`, { method: "PATCH", json: { role, permissions } })
        : await api<WorkspaceUser>("/users", { method: "POST", json: { email, role, permissions } });
      upsert(u);
      setModal(null);
      flash(
        editing
          ? t("Доступ пользователя {name} изменён на «{role}»", { name: u.name, role: roleLabel(u.role) })
          : t("{email} добавлен как «{role}» — теперь он может войти через Google с этой почтой", { email: u.email, role: roleLabel(u.role) }),
      );
    } catch (err) {
      flash(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (u: WorkspaceUser, status: "active" | "disabled") => {
    try {
      upsert(await api<WorkspaceUser>(`/users/${u.id}`, { method: "PATCH", json: { status } }));
      flash(status === "disabled" ? t("Доступ пользователя {name} отозван — он выйдет из Hub при следующем действии", { name: u.name }) : t("Доступ пользователя {name} возвращён", { name: u.name }));
    } catch (err) {
      flash(apiErrorText(err));
    }
  };

  const editing = modal && modal !== "add" ? modal : null;

  const found = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => `${u.name} ${u.email} ${roleLabel(u.role)}`.toLowerCase().includes(q));
  }, [users, query]);

  return (
    <Band id="user-settings" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} / {t("рабочее пространство → настройки пользователей")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {t("Пользователей: {n}", { n: users.filter((u) => u.status !== "disabled").length })}
          </span>
        </div>

        <div className="grid lg:grid-cols-[14.5rem_minmax(0,1fr)]">
          {/* ── настройки ── */}
          <div className="hidden flex-col gap-4 border-border p-3 lg:flex lg:border-e">
            {aiNav.map((g) => (
              <div key={g.group} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.group}</p>
                {g.items.map((it) => {
                  const active = it.id === "user-settings";
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onNav(it.id)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] font-medium transition",
                        active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(it.icon, "text-base leading-none")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{it.label}</span>
                      {it.badge ? (
                        <span className="rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-600">{it.badge}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="mt-auto rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <i className="ri-shield-check-line text-sm leading-none" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("Добавлять пользователей могут владелец и администраторы (менеджер с правом «Управление участниками» — агентов). Владельца отключить нельзя.")}
              </p>
            </div>
          </div>

          {/* ── содержимое окна ── */}
          <div className="min-w-0">
            <div className="flex gap-1.5 overflow-x-auto border-b border-border px-3.5 py-2 lg:hidden">
              {aiNav.flatMap((g) => g.items).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onNav(it.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    it.id === "user-settings" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <i className={it.icon} aria-hidden />
                  {it.label}
                </button>
              ))}
            </div>

            <div className="p-3.5 md:p-5">
              <div className="flex flex-wrap items-start gap-2.5">
                <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                  <i className="ri-user-settings-line text-base leading-none" />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-semibold leading-snug">{t("Настройки пользователей")}</h3>
                  <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                    {t("Управляйте участниками рабочего пространства и приглашайте новых.")}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
                {me?.can.manageUsers ? (
                  <button
                    type="button"
                    onClick={() => setModal("add")}
                    className="inline-flex h-9 flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110"
                  >
                    <i className="ri-user-add-line text-sm leading-none" aria-hidden />
                    {t("Добавить пользователя")}
                  </button>
                ) : (
                  <p className="text-[11.5px] text-muted-foreground">{t("Добавлять и менять пользователей могут владелец и администраторы.")}</p>
                )}
                <div className="relative sm:ms-auto sm:w-64">
                  <i className="ri-search-line pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-sm leading-none text-muted-foreground" aria-hidden />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("Поиск по настройкам пользователей")}
                    aria-label={t("Поиск по пользователям")}
                    className="h-9 w-full rounded-btn border border-border bg-background ps-8 pe-3 text-[12px] placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {clients ? (
                <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted-foreground" data-testid="clients-free">
                  <i className="ri-user-unfollow-line text-amber-600" aria-hidden />
                  <span>
                    {t("Без ответственного")}: {clientsLine(clients.free)} · {dialogsLine(clients.free.dialogs)}
                  </span>
                  <button type="button" onClick={() => goAnchor("#companies")} className="cursor-pointer font-semibold text-primary hover:underline">
                    {t("открыть компании")}
                  </button>
                </p>
              ) : null}
              {loading ? (
                <p className="mt-4 text-[12px] text-muted-foreground">{t("Загружаем пользователей…")}</p>
              ) : found.length === 0 ? (
                <div className="mt-4 rounded-btn border border-dashed border-border bg-background/40 px-4 py-10 text-center">
                  <p className="text-[13px] font-semibold">{t("По запросу «{q}» никого не нашли", { q: query.trim() })}</p>
                  <p className="mt-1 text-[12px] text-muted-foreground">{t("Попробуйте другую почту или имя, либо очистите поиск.")}</p>
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="mt-3 inline-flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3 text-[11.5px] font-semibold transition hover:border-primary/50 hover:text-primary"
                  >
                    <i className="ri-refresh-line text-[13px] leading-none" aria-hidden />
                    {t("Сбросить поиск")}
                  </button>
                </div>
              ) : (
                <ul className="mt-4 flex flex-col gap-2">
                  {found.map((u) => (
                    <li
                      key={u.id}
                      className={cn(
                        "flex flex-wrap items-center gap-3 rounded-btn border border-border bg-background/50 p-3 transition hover:border-primary/40",
                        u.status === "disabled" && "opacity-60",
                      )}
                    >
                      <span className="relative grid size-9 flex-none place-items-center rounded-full bg-primary/12 text-[11px] font-bold text-primary" aria-hidden>
                        {initials(u.name)}
                        {u.online ? (
                          <span className="absolute -end-0.5 -bottom-0.5 size-2.5 rounded-full border-2 border-card bg-emerald-500" />
                        ) : null}
                      </span>

                      <div className="min-w-0 flex-1">
                        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] font-semibold leading-snug">
                          <span className="truncate">{u.name}</span>
                          {u.id === me?.id ? <span className="text-[10.5px] font-medium text-muted-foreground">{t("(вы)")}</span> : null}
                          {u.owner ? (
                            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-primary">
                              {t("владелец")}
                            </span>
                          ) : null}
                          {u.status === "invited" ? (
                            <span className="rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-amber-700">{t("добавлен, не входил")}</span>
                          ) : null}
                          {u.status === "disabled" ? (
                            <span className="rounded-full bg-foreground/[.08] px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-muted-foreground">{t("отключён")}</span>
                          ) : null}
                        </p>
                        <p className="mt-0.5 truncate text-[11.5px] text-muted-foreground">
                          {roleLabel(u.role)} · {u.email}
                          {u.permissions.length ? ` · ${u.permissions.map(permLabel).join(", ")}` : ""}
                        </p>
                        {clients ? (
                          <p className="mt-0.5 text-[11px] text-muted-foreground">
                            <i className="ri-user-star-line me-1" aria-hidden />
                            {t("Клиенты")}: {clientsLine(clients.users[u.id] ?? EMPTY_CLIENTS)} · {t("видит")} {dialogsLine(clients.users[u.id]?.dialogs ?? 0)}
                            {(clients.users[u.id]?.companies ?? 0) > 0 ? (
                              <button type="button" onClick={() => showCompanies({ id: u.id, name: u.name })} className="ms-1.5 cursor-pointer font-semibold text-primary hover:underline">
                                {t("показать")}
                              </button>
                            ) : null}
                          </p>
                        ) : null}
                      </div>

                      <div className="flex flex-none items-center gap-1.5">
                        <span className="hidden whitespace-nowrap text-[10.5px] text-muted-foreground sm:inline">
                          {lastActive(u)}
                        </span>
                        {me?.can.viewAs && !me.viewingAs && !u.owner && u.status !== "disabled" && u.id !== me.id ? (
                          <button
                            type="button"
                            onClick={() => void viewAs(u.id).catch((e) => flash(apiErrorText(e)))}
                            title={t("Посмотреть Hub глазами этого сотрудника: его клиенты, диалоги и разделы. Вернуться — кнопкой в шапке")}
                            className="inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn px-2 text-[11px] font-semibold text-muted-foreground transition hover:bg-primary/10 hover:text-primary"
                            data-testid={`view-as-${u.email}`}
                          >
                            <i className="ri-eye-line text-[12px] leading-none" aria-hidden />
                            {t("Посмотреть как")}
                          </button>
                        ) : null}
                        {canEdit(u) && u.status !== "disabled" ? (
                          <button
                            type="button"
                            onClick={() => void setStatus(u, "disabled")}
                            className="inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn px-2 text-[11px] font-semibold text-muted-foreground transition hover:bg-red-500/10 hover:text-red-600"
                          >
                            <i className="ri-close-circle-line text-[12px] leading-none" aria-hidden />
                            {t("Отозвать")}
                          </button>
                        ) : null}
                        {canEdit(u) && u.status === "disabled" ? (
                          <button
                            type="button"
                            onClick={() => void setStatus(u, "active")}
                            className="inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn px-2 text-[11px] font-semibold text-muted-foreground transition hover:bg-primary/10 hover:text-primary"
                          >
                            <i className="ri-arrow-go-back-line text-[12px] leading-none" aria-hidden />
                            {t("Вернуть доступ")}
                          </button>
                        ) : null}
                        {canEdit(u) && u.status !== "disabled" ? <button
                          type="button"
                          onClick={() => setModal(u)}
                          aria-label={t("Изменить доступ {name}", { name: u.name })}
                          title={t("Изменить доступ")}
                          className="inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn border border-border px-2 text-[11px] font-semibold transition hover:border-primary/50 hover:text-primary"
                        >
                          <i className="ri-pencil-line text-[12px] leading-none" aria-hidden />
                          {t("Изменить")}
                        </button> : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-4 grid gap-2 border-t border-border/60 pt-3 sm:grid-cols-3">
                {userHints.map((h) => (
                  <div key={h.title} className="rounded-btn border border-border bg-background/40 p-2.5">
                    <p className="flex items-center gap-1.5 text-[11.5px] font-semibold">
                      <i className={cn(h.icon, "text-[13px] leading-none text-primary")} aria-hidden />
                      {h.title}
                    </p>
                    <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{h.text}</p>
                  </div>
                ))}
              </div>

              <p className="mt-3 text-[11px] text-muted-foreground">
                {t("Все изменения пользователей и входы записываются в «Настройки → Журнал действий».")}
              </p>
            </div>

            {toast ? (
              <div
                role="status"
                className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
              >
                <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
                {toast}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {modal ? (
        <UserModal
          mode={editing ? "edit" : "add"}
          user={editing ?? undefined}
          roles={roles}
          busy={busy}
          onClose={() => setModal(null)}
          onSave={(email, role, permissions) => void save(email, role, permissions)}
        />
      ) : null}
    </Band>
  );
}

export default UserSettingsDemo;